/**
 * @module useAsk
 *
 * @remarks
 * Controls the Ask AI feature - open/close state, messages, input focus.
 * Handles message history, streaming responses, and UI state.
 * Fetches a small page-API summary on-demand. Full example/API blobs trip
 * Cloudflare WAF 403 on POST /docs/ask (~4.5KB+).
 */

// Framework
import { IN_BROWSER, useDocumentEventListener, useRaf } from '@vuetify/v0'

// Composables
import { useSettings } from './useSettings'

// Utilities
import { resolveItemName } from '@/utilities/strings'
import { readonly, shallowRef } from 'vue'
import { useRoute } from 'vue-router'

// Types
import type { ApiData } from '@build/generate-api'
import type { Ref, ShallowRef } from 'vue'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

export interface UseAskReturn {
  /** Chat message history */
  messages: Readonly<ShallowRef<readonly Message[]>>
  /** Whether the sheet is open */
  isOpen: Readonly<Ref<boolean>>
  /** Whether a response is being generated */
  isLoading: Readonly<Ref<boolean>>
  /** Current error message */
  error: Readonly<Ref<string | null>>
  /** Submit a question */
  ask: (question: string) => Promise<void>
  /** Clear chat history */
  clear: () => void
  /** Open the sheet */
  open: () => void
  /** Close the sheet */
  close: () => void
  /** Toggle the sheet */
  toggle: () => void
  /** Stop the current streaming response */
  stop: () => void
  /** Request focus on the visible input (floating or sheet) */
  focus: () => void
  /** Focus trigger counter (watch this to focus input) */
  focusTrigger: Readonly<Ref<number>>
}

// Context types sent to API
interface ApiContext {
  name: string
  kind: 'component' | 'composable'
  props?: Array<{ name: string, type: string, required: boolean, default?: string, description?: string }>
  events?: Array<{ name: string, type: string, description?: string }>
  slots?: Array<{ name: string, type?: string, description?: string }>
  options?: Array<{ name: string, type: string, required: boolean, default?: string, description?: string }>
  methods?: Array<{ name: string, type: string, description?: string }>
  properties?: Array<{ name: string, type: string, description?: string }>
}

interface PageContext {
  api?: ApiContext[]
}

const API_URL = `${import.meta.env.VITE_API_SERVER_URL || 'https://api.vuetifyjs.com'}/docs/ask`

/** Harbor: Button ~11.5KB and createFilter ~4.5KB POSTs 403 at CF. Stay well under. */
const CONTEXT_MAX_BYTES = 2048

let messageId = 0
function createId () {
  return `msg-${++messageId}-${Date.now()}`
}

// Caches
let apiCache: ApiData | null = null

async function getApiData (): Promise<ApiData> {
  if (apiCache) return apiCache
  try {
    const response = await fetch('/api.json')
    if (!response.ok) return { components: {}, composables: {} }
    apiCache = await response.json()
    return apiCache!
  } catch {
    return { components: {}, composables: {} }
  }
}

function slimFields (
  fields?: Array<{ name: string, type?: string, required?: boolean, description?: string, default?: string }>,
) {
  if (!fields?.length) {
    return undefined
  }

  return fields.slice(0, 24).map(field => ({
    name: field.name,
    ...(field.type ? { type: field.type } : {}),
    ...(field.required ? { required: true } : {}),
  }))
}

function slimApi (apis: ApiContext[]): ApiContext[] {
  return apis.map(api => ({
    name: api.name,
    kind: api.kind,
    props: slimFields(api.props),
    events: slimFields(api.events),
    slots: slimFields(api.slots),
    options: slimFields(api.options),
    methods: slimFields(api.methods),
    properties: slimFields(api.properties),
  }))
}

function fitContext (context: PageContext): PageContext | undefined {
  if (!context.api?.length) {
    return undefined
  }

  const slimed = { api: slimApi(context.api) }
  if (JSON.stringify(slimed).length <= CONTEXT_MAX_BYTES) {
    return slimed
  }

  const names = { api: slimed.api.map(entry => ({ name: entry.name, kind: entry.kind })) }
  if (JSON.stringify(names).length <= CONTEXT_MAX_BYTES) {
    return names
  }

  return undefined
}

/** Extract the page slug from path for matching examples/api */
function getPageSlug (path: string): string | null {
  // /components/*/step -> step
  // /composables/*/create-selection -> create-selection
  const match = path.match(/\/(components|composables)\/[^/]+\/([^/]+)/)
  return match?.[2] ?? null
}

/** Get API data for a page */
function getApiForPage (path: string, apiData: ApiData): ApiContext[] | undefined {
  const name = resolveItemName(path)
  if (!name) return undefined

  if (path.includes('/components/')) {
    const apis = Object.entries(apiData.components)
      .filter(([key]) => key.startsWith(name))
      .map(([, api]) => ({
        name: api.name,
        kind: 'component' as const,
        props: api.props,
        events: api.events,
        slots: api.slots,
      }))

    return apis.length > 0 ? apis : undefined
  }

  if (path.includes('/composables/')) {
    const api = apiData.composables[name]
    if (api) {
      return [{
        name: api.name,
        kind: 'composable' as const,
        options: api.options,
        methods: api.methods,
        properties: api.properties,
      }]
    }
  }

  return undefined
}

/** Fetch a WAF-safe API summary for the current page. No example source. */
async function fetchPageContext (path: string): Promise<PageContext> {
  if (!IN_BROWSER || path === '/' || path === '') {
    return {}
  }

  const slug = getPageSlug(path)
  if (!slug) {
    return {}
  }

  const apiData = await getApiData()
  const api = getApiForPage(path, apiData)
  if (!api) {
    return {}
  }

  return fitContext({ api }) ?? {}
}

// Module-level singleton state (shared across all useAsk calls)
const messages: ShallowRef<Message[]> = shallowRef([])
const isOpen = shallowRef(false)
const isLoading = shallowRef(false)
const error: ShallowRef<string | null> = shallowRef(null)
const focusTrigger = shallowRef(0)
let abortController: AbortController | null = null
let keyboardRegistered = false

/**
 * Controls the Ask AI feature.
 *
 * @returns Ask state and controls
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 *  import { useAsk } from '@/composables/useAsk'
 *
 *  const ask = useAsk()
 *
 *  // Focus the visible input (floating or sheet)
 *  ask.focus()
 *
 *  // Submit a question
 *  ask.ask('How do I use this component?')
 * </script>
 * ```
 */
export function useAsk (): UseAskReturn {
  const route = useRoute()
  const { packageManager } = useSettings()

  // Register global keyboard shortcuts once (needs component context for useDocumentEventListener)
  if (!keyboardRegistered) {
    keyboardRegistered = true
    useDocumentEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        if (isOpen.value) isOpen.value = false
        else focusTrigger.value++
      }
      if (e.key === 'Escape' && isOpen.value) {
        e.preventDefault()
        isOpen.value = false
      }
    })
  }

  function open () {
    isOpen.value = true
  }

  function close () {
    isOpen.value = false
  }

  function toggle () {
    isOpen.value = !isOpen.value
  }

  function clear () {
    messages.value = []
    error.value = null
  }

  function stop () {
    if (abortController) {
      abortController.abort()
      abortController = null
      isLoading.value = false
    }
  }

  function focus () {
    focusTrigger.value++
  }

  async function ask (question: string) {
    if (!IN_BROWSER || !question.trim()) return
    if (isLoading.value) return

    error.value = null
    isLoading.value = true
    open()

    // Add user message
    const userMessage: Message = {
      id: createId(),
      role: 'user',
      content: question.trim(),
      timestamp: Date.now(),
    }

    // Add assistant placeholder
    const assistantMessage: Message = {
      id: createId(),
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    }

    // Get conversation history (last 6 messages, excluding the new ones)
    const history = messages.value
      .filter(m => m.content.trim()) // Only include messages with content
      .slice(-6)
      .map(m => ({ role: m.role, content: m.content }))

    messages.value = [...messages.value, userMessage, assistantMessage]

    abortController = new AbortController()

    try {
      // Fetch dynamic page context
      const context = await fetchPageContext(route.path)

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: question.trim(),
          path: route.path,
          history: history.length > 0 ? history : undefined,
          context: Object.keys(context).length > 0 ? context : undefined,
          packageManager: packageManager.value,
        }),
        signal: abortController.signal,
      })

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please wait a moment before asking another question.')
        }
        throw new Error('Failed to get response. Please try again.')
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('Streaming not supported')
      }

      const decoder = new TextDecoder()

      // Buffer chunks and batch updates at ~60fps for performance
      let pendingContent = ''

      function flushPendingContent () {
        if (!pendingContent) return

        const lastMessage = messages.value.at(-1)
        if (lastMessage?.role === 'assistant') {
          messages.value = [
            ...messages.value.slice(0, -1),
            { ...lastMessage, content: lastMessage.content + pendingContent },
          ]
        }
        pendingContent = ''
      }

      const flush = useRaf(flushPendingContent)

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        pendingContent += decoder.decode(value, { stream: true })
        flush()
      }

      // Cancel pending frame and flush remaining content
      flush.cancel()
      pendingContent += decoder.decode()
      flushPendingContent()
    } catch (error_) {
      if ((error_ as Error).name === 'AbortError') {
        // User cancelled - keep partial response
        return
      }

      error.value = (error_ as Error).message

      // Remove empty assistant message on error
      const updatedMessages = [...messages.value]
      const lastMessage = updatedMessages.at(-1)
      if (lastMessage?.role === 'assistant' && !lastMessage.content) {
        updatedMessages.pop()
        messages.value = updatedMessages
      }
    } finally {
      isLoading.value = false
      abortController = null
    }
  }

  return {
    messages: readonly(messages),
    isOpen: readonly(isOpen),
    isLoading: readonly(isLoading),
    error: readonly(error),
    focusTrigger: readonly(focusTrigger),
    ask,
    clear,
    open,
    close,
    toggle,
    stop,
    focus,
  }
}
